import axios from "axios";
import { publicIp } from "./apiIp";
import { httpCode, processMessage } from "./helper.js";
import { actionCreators } from "../../common/modal";
import store from "../../store";
import history from "../../utils/history";
import {
  showIntegrationErrorBanner,
  showSubscriptionModal,
} from "../../common/header/store/actionCreators";

const instance = axios.create({
  //axios instance
  timeout: 600000, // delay time 10 mins
  baseURL: publicIp,
});

// post header
instance.defaults.headers.post["Content-Type"] = "application/json";

/** Add request interceptor **/
instance.interceptors.request.use(
  (config) => {
    config.headers["token"] = sessionStorage.getItem("token") || "";
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/** Add response interceptor  **/
instance.interceptors.response.use(
  (response) => {
    // redirect to next page if there's a stored next
    const next = localStorage.getItem("next");
    if (next) {
      localStorage.removeItem("next");
      window.location.replace(next);
    }

    if (response.status === 200 || response.status === 201) {
      //handle redirect link
      if (response.data.status === 301) {
        window.location.href = response.data.data.redirect;
      }
      return Promise.resolve(response);
    } else {
      store.dispatch(
        actionCreators.showModal("alert", {
          open: true,
          title: "Error!",
          message: "Time out, please refresh again!",
        }),
      );
      return Promise.reject(response.data.message);
    }
  },
  async (error) => {
    if (error.response) {
      // According to the http status code of the request
      // failure to give the user the corresponding prompt
      if (error.response.status === 403) {
        history.push(`/access-denied`);
        return;
      }
      let errorMsg = "";
      // not logged in
      if (error.response.status === 401) {
        const currentLocation =
          window.location.pathname + window.location.search;
        // get first slash/location
        const [startsWith] = window.location.pathname
          .split("/")
          .filter((i) => i); // get first slash/location

        const searchParams = new URLSearchParams(window.location.search);

        switch (startsWith) {
          case "project":
          // fall through because they currently do the same thing for now
          case "contract":
            // check if it has "invite_type" param = it's a contract/project invite redirect
            if (searchParams.get("invite_type")) {
              let redirect = `/invite_login?next=${currentLocation}&user_email=${searchParams.get("user_email")}&invite_type=${searchParams.get("invite_type")}`;
              window.location = redirect;
            } else {
              // redirect to login page if it's not an invitation
              window.location =
                "/login?next=" + encodeURIComponent(currentLocation);
            }
            break;
          default:
            window.location =
              "/login?next=" + encodeURIComponent(currentLocation);
        }

        return;
      }

      // Set subscription modal.
      if (error.status === 402) {
        // Dispatch the showSubscriptionModal action
        store.dispatch(showSubscriptionModal(error?.response?.data));
        return Promise.reject(error);
      }

      // custom integration error
      if (error.response.status === 460 || error.response.status === 464) {
        switch (error.response.status) {
          case 460:
            // integration disconnected / default error / token expired
            store.dispatch(
              showIntegrationErrorBanner(error?.response?.data?.data?.errors),
            );
            break;
          default:
            // integration not exist
            break;
        }
        return Promise.reject(error);
      }

      if (error.response.status in httpCode) {
        errorMsg = httpCode[error.response.status];
      } else if (
        error.response.request.responseType === "blob" &&
        error.response.data instanceof Blob &&
        error.response.data.type &&
        error.response.data.type.toLowerCase().indexOf("json") !== -1
      ) {
        const msg = JSON.parse(await error.response.data.text());
        errorMsg = msg.data.errors;
      } else {
        errorMsg = error.response.data.data.errors;
      }
      store.dispatch(
        actionCreators.showModal("alert", {
          open: true,
          title: "Error!",
          message: processMessage(errorMsg),
        }),
      );

      return Promise.reject(error);
    } else {
      store.dispatch(
        actionCreators.showModal("alert", {
          open: true,
          title: "Error!",
          message: "Time out, please refresh again!",
        }),
      );
      return Promise.reject(new Error("Time out, please refresh again!"));
    }
  },
);

export const httpRequest = (method, url, data, config = {}, headers = {}) => {
  const searhParams = new URLSearchParams(window.location.search);
  const ACC_ID =
    searhParams?.get("account_id") ||
    store.getState().getIn(["headers", "profile", "payclaim_account_id"]);

  if (url.indexOf("accId") !== -1) {
    url = url.replace("accId", ACC_ID);
  }

  return instance({
    method,
    url,
    data,
    ...config,
    headers,
  });
};
